/**
 * The domain timeline's four steps — one vocabulary, two screens.
 *
 * c92's named trigger, fired: *"the steps belong in one exported constant the day the
 * second consumer exists."* The artwork screen has rendered this list inline since c92; the
 * domain-setup screen is the second consumer, and this is that constant.
 *
 * ## Here rather than in `packages/shared`, and the reason is not CI cost
 *
 * Both consumers are `apps/app` screens, and nothing outside this app renders a timeline —
 * **locality**, which is the same reason `CopyField` is app-local. `packages/ui` owns the
 * `Timeline` component and `STEP_STATES`; what lives here is the *domain's* four labels,
 * which are product copy rather than a primitive.
 *
 * (`packages/shared` would also put screen copy on `conformance.yml`'s `paths:`, billing a
 * GS1 conformance run on every wording tweak. That is true and it is a **second** reason,
 * not the first — `packages/ui` is *not* in those paths, so CI cost is zero there and must
 * not be the remembered rule for placing a primitive.)
 *
 * ## The labels are `components.md`'s, and the details are per-occurrence
 *
 * c92's ruling, unchanged: the fourth step is **"Answering scans"** — `components.md`'s
 * name — because that is the file the one-component-two-screens rule lives in. The artwork
 * mock calls the same step "Serving", and two names for one step in one handoff is the
 * design package's fourth recorded self-inconsistency. `detail` is the per-occurrence field
 * and is where the screens legitimately differ.
 */
import type { StepState, TimelineStep } from "@packagepage/ui";

/** The four steps, in order. A timeline IS an ordering; the statuses behind it are not. */
export const DOMAIN_TIMELINE_STEPS = [
  { id: "added", label: "Domain added" },
  { id: "record", label: "Looking for your record" },
  { id: "certificate", label: "Certificate issued" },
  { id: "serving", label: "Answering scans" },
] as const;

export type DomainTimelineStepId = (typeof DOMAIN_TIMELINE_STEPS)[number]["id"];

/**
 * How far along a delegation is, expressed as the index of the step now in progress.
 *
 * **This is the shared derivation, and it takes FACTS rather than a status**, which is what
 * makes it serve both consumers instead of one. The domains screen holds a `domains` row
 * and derives the facts from it; the artwork screen holds one moment — a domain that has
 * never served, waiting on DNS — and states the same facts directly. Neither is a special
 * case of the other, and neither screen keeps its own state logic.
 *
 * A record rather than an index comparison over `DOMAIN_STATUSES`: that set is not a ladder
 * (U11's 🔴 — `error` sits after `live`), and the timeline's ordering is a property of the
 * timeline, not of the vocabulary.
 */
export interface DomainProgress {
  /** DNS resolves to our edge. */
  readonly recordFound: boolean;
  /** A certificate has been issued for this hostname. */
  readonly certificateIssued: boolean;
  /** It has served at least once — `first_live_at`, which is never cleared. */
  readonly everServed: boolean;
}

/** The step now in progress, as an index into {@link DOMAIN_TIMELINE_STEPS}. */
export function currentStepIndex(progress: DomainProgress): number {
  if (progress.everServed) return 3;
  if (progress.certificateIssued) return 2;
  if (progress.recordFound) return 1;
  // Step 0 is "Domain added", which is done the moment a row exists — so the step in
  // progress while waiting on DNS is step 1, never step 0.
  return 1;
}

/**
 * The four steps with their states resolved, ready for `packages/ui`'s `Timeline`.
 *
 * `details` is supplied per occurrence: the same four labels mean different things to a
 * reader deciding whether to wait for a print file and to one setting up a domain.
 */
export function domainTimeline(
  progress: DomainProgress,
  details: Partial<Record<DomainTimelineStepId, string>> = {},
): readonly TimelineStep[] {
  return resolveTimeline(DOMAIN_TIMELINE_STEPS, currentStepIndex(progress), details);
}

/**
 * Steps before `current` are done, `current` is in progress, the rest are future. A `current`
 * past the last step means every step is done. Shared with the prefix wait — `components.md`:
 * *"One component, two screens."*
 */
export function resolveTimeline<Id extends string>(
  steps: readonly { readonly id: Id; readonly label: string }[],
  current: number,
  details: Partial<Record<Id, string>> = {},
): readonly TimelineStep[] {
  return steps.map((step, index) => {
    const state: StepState =
      index < current ? "done" : index === current ? "current" : "future";
    const detail = details[step.id];
    return detail === undefined
      ? { label: step.label, state }
      : { label: step.label, state, detail };
  });
}

/** A delegation that has never served and is waiting on DNS — the artwork screen's moment. */
export const WAITING_ON_DNS: DomainProgress = {
  recordFound: false,
  certificateIssued: false,
  everServed: false,
};
